import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AdditionalAssetMeta } from "@amzn/afxtaco-types";

/**
 * Per-project state for "additional" assets — the arbitrary, non-canonical files
 * the agent generated (e.g. `research/notes.md`, a `.csv`). Kept in a dedicated
 * slice because these are keyed by relative PATH (an arbitrary string), which the
 * canonical `projectAssets` cache (keyed by the `TacoAssetType` enum) cannot hold.
 */
export interface ProjectAdditionalAssets {
  /** The asset list for the project, or undefined until first fetched. */
  list?: AdditionalAssetMeta[];
  /** Rendered text content keyed by asset path (for renderable assets). */
  content: Record<string, string>;
}

export interface AdditionalAssetsState {
  /** projectId -> additional-asset state. */
  byProject: Record<string, ProjectAdditionalAssets>;
}

const initialState: AdditionalAssetsState = {
  byProject: {},
};

function ensureProject(
  state: AdditionalAssetsState,
  projectId: string,
): ProjectAdditionalAssets {
  if (!state.byProject[projectId]) {
    state.byProject[projectId] = { content: {} };
  }
  return state.byProject[projectId];
}

const AdditionalAssetsSlice = createSlice({
  name: "AdditionalAssets",
  initialState,
  reducers: {
    /** Store (or replace) the asset list for a project. */
    setAdditionalAssetList(
      state,
      action: PayloadAction<{
        projectId: string;
        assets: AdditionalAssetMeta[];
      }>,
    ) {
      const { projectId, assets } = action.payload;
      const project = ensureProject(state, projectId);
      project.list = assets;
    },
    /** Cache the rendered text content of a single additional asset. */
    setAdditionalAssetContent(
      state,
      action: PayloadAction<{
        projectId: string;
        path: string;
        content: string;
      }>,
    ) {
      const { projectId, path, content } = action.payload;
      const project = ensureProject(state, projectId);
      project.content[path] = content;
    },
    /** Drop all additional-asset state for a project (e.g. on stale refresh). */
    clearAdditionalAssets(state, action: PayloadAction<{ projectId: string }>) {
      delete state.byProject[action.payload.projectId];
    },
  },
});

export const {
  setAdditionalAssetList,
  setAdditionalAssetContent,
  clearAdditionalAssets,
} = AdditionalAssetsSlice.actions;

export default AdditionalAssetsSlice.reducer;
